import React from 'react';
import { Box, Typography, Avatar, Chip, Tooltip, IconButton } from '@mui/material';
import { LinkOutlined, Delete, Edit } from '@mui/icons-material';

const TeamSlot = ({ entity, label, score }) => {
  const name = entity?.name || label;
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1,
      '&:first-of-type': { borderBottom: '1px solid', borderColor: 'divider' },
    }}>
      <Avatar src={entity?.logo} sx={{ width: 22, height: 22, fontSize: 9, bgcolor: entity ? 'primary.main' : 'grey.300' }}>
        {!entity?.logo && initials}
      </Avatar>
      <Typography variant="caption" fontWeight={entity ? 600 : 400} color={entity ? 'text.primary' : 'text.disabled'}
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </Typography>
      {score !== undefined && (
        <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 16, textAlign: 'center' }}>
          {score}
        </Typography>
      )}
    </Box>
  );
};

export const BracketCard = ({
  gameEntry, stageOrder, onEdit, onDelete, onLinkClick, isLinking, canEdit = false, sx = {}
}) => {
  const { entity1Id, entity2Id, gameId, winnerFeedsInto } = gameEntry;
  const score = gameId?.score;
  const status = gameId?.status;
  const hasLink = !!winnerFeedsInto?.gameEntryId;

  const statusColors = { live: '#ef4444', completed: '#22c55e', scheduled: '#3b82f6', postponed: '#f59e0b' };
  const borderColor = isLinking ? '#7c3aed' : (hasLink ? '#22c55e33' : 'divider');

return (
    <Box sx={{
      border: '2px solid',
      borderColor,
      borderRadius: 2,
      bgcolor: 'background.paper',
      overflow: 'hidden',
      minWidth: 170,
      maxWidth: 200,
      cursor: isLinking ? 'crosshair' : 'default',
      transition: 'all 0.2s ease',
      boxShadow: isLinking ? '0 0 0 3px #7c3aed44' : '0 1px 4px rgba(0,0,0,0.08)',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.14)', transform: 'translateY(-1px)' },
      ...sx,
    }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1, py: 0.4, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
        {status && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColors[status] || 'grey.400',
            animation: status === 'live' ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
        )}
        <Box sx={{ flex: 1 }} />
        {hasLink && <Tooltip title="Lié à l'étape suivante"><LinkOutlined sx={{ fontSize: 13, color: 'success.main' }} /></Tooltip>}
        <Tooltip title={canEdit ? 'Définir le lien' : 'Super Admin uniquement'}>
          <span>
            <IconButton size="small" onClick={() => onLinkClick(gameEntry)} disabled={!canEdit} sx={{ p: 0.3 }}>
              <LinkOutlined sx={{ fontSize: 13, color: isLinking ? 'secondary.main' : 'action.active' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canEdit ? 'Modifier' : 'Super Admin uniquement'}>
          <span>
            <IconButton size="small" onClick={() => onEdit(gameEntry)} disabled={!canEdit} sx={{ p: 0.3 }}>
              <Edit sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canEdit ? 'Supprimer' : 'Super Admin uniquement'}>
          <span>
            <IconButton size="small" color="error" onClick={() => onDelete(gameEntry._id)} disabled={!canEdit} sx={{ p: 0.3 }}>
              <Delete sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <TeamSlot entity={entity1Id} label="À définir" score={score?.entity1} />
      <TeamSlot entity={entity2Id} label="À définir" score={score?.entity2} />

      {gameId?.categorie && (
        <Box sx={{ px: 1, py: 0.3, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
            {gameId.categorie.replace(/_/g, ' ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
